import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendUp, ArrowLeft } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { extractApiError } from '@/lib/apiErrors';
import Logo from '@/components/Logo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswordResetPage = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSentMode, setEmailSentMode] = useState(false);

  // If URL contains ?token=..., jump straight to confirm step
  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
      setStep('confirm');
    }
  }, [searchParams]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email?.trim()) { toast.error(t('register.error.email_required')); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.error(t('register.error.email_invalid')); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/password-reset-request`, { email });
      toast.success(t('reset.success'));
      if (res.data.reset_token) {
        // Dev/fallback mode: backend returned token (no email delivery configured)
        setToken(res.data.reset_token);
        setStep('confirm');
      } else {
        // Email was sent — instruct user to check inbox
        setEmailSentMode(true);
      }
    } catch (error) {
      toast.error(extractApiError(error, language === 'tr' ? 'Sıfırlama bağlantısı gönderilemedi' : 'Failed to send reset link', language));
    } finally { setLoading(false); }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) { toast.error(t('register.error.password_short')); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/password-reset-confirm`, {
        token,
        new_password: newPassword
      });
      toast.success(t('reset.confirm_success'));
      setStep('done');
    } catch (error) {
      toast.error(extractApiError(error, language === 'tr' ? 'Sıfırlama başarısız' : 'Reset failed', language));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-12">
          <Link to="/" className="flex items-center gap-2">
            <Logo variant="light" size="md" />
          </Link>
          <LanguageSwitcher />
        </div>

        {step === 'request' && (
          <>
            <h1 className="heading-2 text-[#09090B] mb-2" data-testid="reset-title">
              {t('reset.title')}
            </h1>
            <p className="text-[#52525B] mb-8">{t('reset.subtitle')}</p>
            {emailSentMode ? (
              <div className="bg-[#F4F4F5] border border-[#E4E4E7] p-6" data-testid="reset-email-sent">
                <p className="text-[#09090B] font-medium mb-2">{t('reset.success')}</p>
                <p className="text-[#52525B] text-sm">{t('reset.check_email') || 'If this email is registered, a password reset link has been sent. Please check your inbox.'}</p>
              </div>
            ) : (
              <form onSubmit={handleRequestReset} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('reset.email')}</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-none border-[#E4E4E7]" required data-testid="reset-email-input"
                    placeholder={t('login.email.placeholder')} />
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full h-12 rounded-none bg-[#0F1B36] hover:bg-[#00227A] text-white"
                  data-testid="reset-submit-btn"
                >
                  {loading ? t('reset.submitting') : t('reset.submit')}
                </Button>
              </form>
            )}
          </>
        )}

        {step === 'confirm' && (
          <>
            <h1 className="heading-2 text-[#09090B] mb-2">{t('reset.title')}</h1>
            <form onSubmit={handleConfirmReset} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="new_password">{t('reset.new_password')}</Label>
                <Input id="new_password" type="password" value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-12 rounded-none border-[#E4E4E7]" required minLength={6}
                  data-testid="reset-new-password-input" placeholder="••••••••" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-12 rounded-none bg-[#0F1B36] hover:bg-[#00227A] text-white"
                data-testid="reset-confirm-btn"
              >
                {loading ? t('reset.submitting') : t('reset.confirm_submit')}
              </Button>
            </form>
          </>
        )}

        {step === 'done' && (
          <div className="text-center" data-testid="reset-done">
            <h1 className="heading-2 text-[#09090B] mb-4">{t('reset.confirm_success')}</h1>
            <Link to="/login" className="btn-primary inline-flex items-center gap-2">
              <ArrowLeft size={16} />{t('reset.back_login')}
            </Link>
          </div>
        )}

        <p className="mt-8 text-center">
          <Link to="/login" className="text-[#0F1B36] hover:text-[#00227A] font-medium flex items-center justify-center gap-2">
            <ArrowLeft size={16} />{t('reset.back_login')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default PasswordResetPage;
